// frontend/src/components/dashboard/OperationalExcelTable.tsx
import React, { useState, useEffect, useMemo } from 'react';
import { dashboardService } from '../../services/dashboardService';
import { formatCurrency } from '../../utils/format';
import { Table, Calendar, DollarSign, TrendingUp, FileSpreadsheet, RefreshCw, Download } from 'lucide-react';
import * as XLSX from 'xlsx';

interface StoreMetrics {
  efectivo: number;
  tarjeta: number;
  transferencia: number;
  mercado_pago: number;
  kueski: number;
  total: number;
}

interface MetricRow {
  period: string;
  total: number;
  stores: Record<string, StoreMetrics>;
}

export const OperationalExcelTable: React.FC = () => {
  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState<number>(now.getMonth());
  const [selectedYear, setSelectedYear] = useState<number>(now.getFullYear());
  const [viewMode, setViewMode] = useState<'daily' | 'monthly'>('daily');

  const [storeNames, setStoreNames] = useState<string[]>([]);
  const [dailyData, setDailyData] = useState<MetricRow[]>([]);
  const [monthlyData, setMonthlyData] = useState<MetricRow[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const months = [
    { value: 0, label: 'Enero' }, { value: 1, label: 'Febrero' }, { value: 2, label: 'Marzo' },
    { value: 3, label: 'Abril' }, { value: 4, label: 'Mayo' }, { value: 5, label: 'Junio' },
    { value: 6, label: 'Julio' }, { value: 7, label: 'Agosto' }, { value: 8, label: 'Septiembre' },
    { value: 9, label: 'Octubre' }, { value: 10, label: 'Noviembre' }, { value: 11, label: 'Diciembre' },
  ];
  const years = Array.from({ length: 5 }, (_, i) => now.getFullYear() - 2 + i);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await dashboardService.getOperationalMetricsExcel(selectedMonth, selectedYear);
      setStoreNames(data.storeNames || []);
      setDailyData(data.dailyMetrics);
      setMonthlyData(data.monthlyMetrics);
    } catch (err: any) {
      console.error('Error fetching operational excel metrics:', err);
      setError('Error al cargar las métricas operativas. Intente nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedMonth, selectedYear]);

  const currentTableData = viewMode === 'daily' ? dailyData : monthlyData;

  const totalsByStore = useMemo(() => {
    const acc: Record<string, StoreMetrics> = {};
    for (const name of storeNames) {
      acc[name] = { efectivo: 0, tarjeta: 0, transferencia: 0, mercado_pago: 0, kueski: 0, total: 0 };
    }
    let grandTotal = 0;
    for (const row of currentTableData) {
      grandTotal += row.total;
      for (const name of storeNames) {
        if (row.stores[name]) {
          acc[name].efectivo += row.stores[name].efectivo;
          acc[name].tarjeta += row.stores[name].tarjeta;
          acc[name].transferencia += row.stores[name].transferencia;
          acc[name].mercado_pago += row.stores[name].mercado_pago;
          acc[name].kueski += row.stores[name].kueski;
          acc[name].total += row.stores[name].total;
        }
      }
    }
    return { stores: acc, total: grandTotal };
  }, [currentTableData, storeNames]);

  const activeStoreNames = useMemo(() => {
    return storeNames.filter(name => {
      const storeTotals = totalsByStore.stores[name];
      return storeTotals && (storeTotals.total > 0 || storeTotals.efectivo > 0 || storeTotals.tarjeta > 0 || storeTotals.transferencia > 0 || storeTotals.mercado_pago > 0 || storeTotals.kueski > 0);
    });
  }, [storeNames, totalsByStore]);

  const handleExportExcel = () => {
    const wb = XLSX.utils.book_new();
    const wsData: any[][] = [];

    // Título / Periodo
    const periodName = viewMode === 'daily' ? `Diario - ${months[selectedMonth].label} ${selectedYear}` : `Mensual - ${selectedYear}`;
    wsData.push([`Reporte Operativo de Ventas: ${periodName}`]);
    wsData.push([]);

    // Headers - Nombres de tiendas
    const headerRow1 = [viewMode === 'daily' ? 'Fecha' : 'Mes'];
    activeStoreNames.forEach(store => {
      headerRow1.push(store, '', '', '', '', '');
    });
    headerRow1.push('Total General');
    wsData.push(headerRow1);

    // Sub-headers - Métodos de pago
    const headerRow2 = [''];
    activeStoreNames.forEach(() => {
      headerRow2.push('Efectivo', 'TC', 'Transf.', 'Mercado P.', 'Kueski', 'Total Tienda');
    });
    headerRow2.push('');
    wsData.push(headerRow2);

    // Filas de datos
    currentTableData.forEach(row => {
      const rowData: any[] = [row.period];
      activeStoreNames.forEach(store => {
        const metrics = row.stores[store] || { efectivo: 0, tarjeta: 0, transferencia: 0, mercado_pago: 0, kueski: 0, total: 0 };
        rowData.push(metrics.efectivo, metrics.tarjeta, metrics.transferencia, metrics.mercado_pago, metrics.kueski, metrics.total);
      });
      rowData.push(row.total);
      wsData.push(rowData);
    });

    // Fila de totales
    wsData.push([]);
    const totalRow: any[] = ['TOTALES'];
    activeStoreNames.forEach(store => {
      const metrics = totalsByStore.stores[store] || { efectivo: 0, tarjeta: 0, transferencia: 0, mercado_pago: 0, kueski: 0, total: 0 };
      totalRow.push(metrics.efectivo, metrics.tarjeta, metrics.transferencia, metrics.mercado_pago, metrics.kueski, metrics.total);
    });
    totalRow.push(totalsByStore.total);
    wsData.push(totalRow);

    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // Merges para cabeceras
    const merges = [];
    merges.push({ s: { r: 0, c: 0 }, e: { r: 0, c: 1 + (activeStoreNames.length * 6) } });
    
    // Merge para nombres de tiendas
    let currentCol = 1;
    activeStoreNames.forEach(() => {
      merges.push({ s: { r: 2, c: currentCol }, e: { r: 2, c: currentCol + 5 } });
      currentCol += 6;
    });
    ws['!merges'] = merges;

    XLSX.utils.book_append_sheet(wb, ws, 'Métricas Operativas');
    XLSX.writeFile(wb, `Reporte_Operativo_${viewMode === 'daily' ? 'Diario' : 'Mensual'}_${Date.now()}.xlsx`);
  };

  const renderHeaderAndFilters = () => (
    <>
      {/* Top Header Bar */}
      <div className="p-6 bg-gradient-to-r from-emerald-800 to-emerald-900 rounded-t-2xl flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-white/10 rounded-xl border border-white/20 backdrop-blur-sm text-white">
            <FileSpreadsheet className="w-6 h-6 text-emerald-400" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-white tracking-wide flex items-center gap-2">
              Métricas Operativas de Ventas
            </h2>
            <p className="text-emerald-200 text-xs mt-0.5">
              Desglose de ingresos reales por día, mes y tiendas
            </p>
          </div>
        </div>

        {/* View mode buttons, Refresh & Export */}
        <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
          <div className="flex bg-emerald-950/60 p-1 rounded-xl border border-white/10 w-full md:w-auto">
            <button
              onClick={() => setViewMode('daily')}
              className={`flex-1 md:flex-none px-4 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-1.5 ${
                viewMode === 'daily'
                  ? 'bg-white text-emerald-900 shadow-sm'
                  : 'text-emerald-100 hover:text-white hover:bg-white/10'
              }`}
            >
              <Calendar className="w-3.5 h-3.5" />
              <span>Días del Mes</span>
            </button>
            <button
              onClick={() => setViewMode('monthly')}
              className={`flex-1 md:flex-none px-4 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-1.5 ${
                viewMode === 'monthly'
                  ? 'bg-white text-emerald-900 shadow-sm'
                  : 'text-emerald-100 hover:text-white hover:bg-white/10'
              }`}
            >
              <Table className="w-3.5 h-3.5" />
              <span>Meses del Año</span>
            </button>
          </div>

          <button
            onClick={handleExportExcel}
            className="flex-1 md:flex-none px-4 py-1.5 bg-emerald-500 text-white font-bold text-xs rounded-xl hover:bg-emerald-400 transition-all shadow-sm border border-emerald-400/50 flex items-center justify-center gap-1.5"
            title="Descargar Excel"
          >
            <Download className="w-4 h-4" />
            Descargar Excel
          </button>

          <button
            onClick={fetchData}
            disabled={loading}
            className="p-2 bg-white/10 border border-white/20 text-white rounded-xl hover:bg-white/20 transition-all disabled:opacity-50 flex items-center justify-center shadow-sm"
            title="Actualizar tabla"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="px-6 py-4 bg-gray-50 border-b border-gray-200 flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1">
            <Calendar className="w-3.5 h-3.5 text-gray-400" />
            <span>Seleccionar Periodo:</span>
          </span>
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(Number(e.target.value))}
            className="bg-white border border-gray-300 text-gray-800 text-xs font-semibold rounded-lg focus:ring-emerald-500 focus:border-emerald-500 py-1.5 px-3 shadow-sm outline-none cursor-pointer hover:border-gray-400 transition-colors"
          >
            {months.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>

          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            className="bg-white border border-gray-300 text-gray-800 text-xs font-semibold rounded-lg focus:ring-emerald-500 focus:border-emerald-500 py-1.5 px-3 shadow-sm outline-none cursor-pointer hover:border-gray-400 transition-colors"
          >
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>

        <div className="text-xs font-medium text-gray-500 flex items-center gap-1.5">
          <TrendingUp className="w-4 h-4 text-emerald-600" />
          <span>Viendo: {viewMode === 'daily' ? `Días de ${months[selectedMonth].label} ${selectedYear}` : `Todos los meses de ${selectedYear}`}</span>
        </div>
      </div>
    </>
  );

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
      {error ? (
        <>
          {renderHeaderAndFilters()}
          <div className="p-8 text-center text-red-600 bg-red-50 font-medium text-sm">
            {error}
          </div>
        </>
      ) : (
        <div className="w-full overflow-x-auto">
          <table className="w-full text-left border-separate border-spacing-0 min-w-max">
            <thead className="sticky top-0 z-30 shadow-md bg-white">
              <tr>
                <td colSpan={2 + (activeStoreNames.length * 6)} className="p-0 border-none bg-white">
                  {renderHeaderAndFilters()}
                </td>
              </tr>
              {/* Store Headers */}
              <tr className="bg-emerald-50 text-emerald-900 text-[11px] font-extrabold uppercase tracking-wider">
                <th className="py-2 px-6 border-b border-gray-200 border-r" rowSpan={2}>{viewMode === 'daily' ? 'Fecha' : 'Mes'}</th>
                {activeStoreNames.map(store => (
                  <th key={store} colSpan={6} className="py-2 px-6 text-center border-b border-gray-200 border-r">{store}</th>
                ))}
                <th className="py-2 px-6 text-center border-b border-gray-200 bg-emerald-100" rowSpan={2}>Total General</th>
              </tr>
              {/* Metric Headers */}
              <tr className="bg-gray-100 border-b border-gray-200 text-gray-600 text-[10px] font-bold uppercase tracking-wider">
                {activeStoreNames.map((_, i) => (
                  <React.Fragment key={`metrics-${i}`}>
                    <th className="py-2 px-3 text-right border-b border-gray-200">Efe</th>
                    <th className="py-2 px-3 text-right border-b border-gray-200">TC</th>
                    <th className="py-2 px-3 text-right border-b border-gray-200">Transf</th>
                    <th className="py-2 px-3 text-right border-b border-gray-200" title="Mercado Pago">MP</th>
                    <th className="py-2 px-3 text-right border-b border-gray-200">Kueski</th>
                    <th className="py-2 px-3 text-right text-emerald-800 border-b border-gray-200 border-r">Total</th>
                  </React.Fragment>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 text-xs font-medium text-gray-700">
              {loading && currentTableData.length === 0 ? (
                <tr>
                  <td colSpan={2 + (activeStoreNames.length * 6)} className="py-12 text-center text-gray-400 font-medium">
                    <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2 text-emerald-600" />
                    Cargando métricas operativas...
                  </td>
                </tr>
              ) : currentTableData.length === 0 ? (
                <tr>
                  <td colSpan={2 + (activeStoreNames.length * 6)} className="py-12 text-center text-gray-400 font-medium">
                    No se encontraron datos para el periodo seleccionado.
                  </td>
                </tr>
              ) : (
                currentTableData.map((row, index) => {
                  const hasValues = row.total > 0;
                  return (
                    <tr
                      key={index}
                      className={`hover:bg-emerald-50/40 transition-colors ${
                        hasValues ? 'bg-white' : 'bg-gray-50/30 text-gray-400'
                      }`}
                    >
                      <td className="py-2 px-6 font-bold text-gray-900 flex items-center gap-2 border-r border-gray-100">
                        <span className={`w-1.5 h-1.5 rounded-full ${hasValues ? 'bg-emerald-500' : 'bg-gray-300'}`} />
                        {row.period}
                      </td>
                      {activeStoreNames.map((store, i) => {
                        const m = row.stores[store] || { efectivo: 0, tarjeta: 0, transferencia: 0, mercado_pago: 0, kueski: 0, total: 0 };
                        return (
                          <React.Fragment key={`data-${i}`}>
                            <td className="py-2 px-3 text-right font-semibold">{m.efectivo > 0 ? formatCurrency(m.efectivo) : '-'}</td>
                            <td className="py-2 px-3 text-right font-semibold">{m.tarjeta > 0 ? formatCurrency(m.tarjeta) : '-'}</td>
                            <td className="py-2 px-3 text-right font-semibold">{m.transferencia > 0 ? formatCurrency(m.transferencia) : '-'}</td>
                            <td className="py-2 px-3 text-right font-semibold text-gray-400">{m.mercado_pago > 0 ? formatCurrency(m.mercado_pago) : '-'}</td>
                            <td className="py-2 px-3 text-right font-semibold text-gray-400">{m.kueski > 0 ? formatCurrency(m.kueski) : '-'}</td>
                            <td className="py-2 px-3 text-right font-bold text-emerald-800 border-r border-gray-100 bg-emerald-50/30">
                              {m.total > 0 ? formatCurrency(m.total) : '-'}
                            </td>
                          </React.Fragment>
                        );
                      })}
                      <td className="py-2 px-6 text-right font-extrabold text-emerald-900 bg-emerald-100/40">
                        {row.total > 0 ? formatCurrency(row.total) : '-'}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
            {/* Total Row */}
            <tfoot className="bg-emerald-50 shadow-sm sticky bottom-0 z-20">
              <tr className="border-t-2 border-emerald-500 text-emerald-950 font-black text-[11px] uppercase tracking-wider shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)]">
                <td className="py-3 px-6 flex items-center gap-1.5 border-r border-emerald-200">
                  <DollarSign className="w-4 h-4 text-emerald-600" />
                  <span>TOTALES</span>
                </td>
                {activeStoreNames.map((store, i) => {
                  const m = totalsByStore.stores[store] || { efectivo: 0, tarjeta: 0, transferencia: 0, mercado_pago: 0, kueski: 0, total: 0 };
                  return (
                    <React.Fragment key={`total-${i}`}>
                      <td className="py-3 px-3 text-right text-emerald-900 font-extrabold border-t border-emerald-200">{formatCurrency(m.efectivo)}</td>
                      <td className="py-3 px-3 text-right text-emerald-900 font-extrabold border-t border-emerald-200">{formatCurrency(m.tarjeta)}</td>
                      <td className="py-3 px-3 text-right text-emerald-900 font-extrabold border-t border-emerald-200">{formatCurrency(m.transferencia)}</td>
                      <td className="py-3 px-3 text-right text-emerald-700 font-bold border-t border-emerald-200">{formatCurrency(m.mercado_pago)}</td>
                      <td className="py-3 px-3 text-right text-emerald-700 font-bold border-t border-emerald-200">{formatCurrency(m.kueski)}</td>
                      <td className="py-3 px-3 text-right text-emerald-950 font-black border-r border-t border-emerald-200 bg-emerald-100/50">
                        {formatCurrency(m.total)}
                      </td>
                    </React.Fragment>
                  );
                })}
                <td className="py-3 px-6 text-right text-emerald-950 font-black text-sm border-t border-emerald-200 bg-emerald-200/50">
                  {formatCurrency(totalsByStore.total)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
};
